import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  API,
  hasBackend,
  buildMailto,
  CONTACT_EMAIL,
  CONTACT_PHONE,
  CONTACT_PHONE_HREF,
} from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    topic: "PV-Anlage Privat",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const resetForm = () =>
    setForm({
      name: "",
      email: "",
      phone: "",
      topic: "PV-Anlage Privat",
      message: "",
    });

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || form.message.trim().length < 5) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus.");
      return;
    }

    // Ohne konfiguriertes Backend direkt ins E-Mail-Programm ausweichen,
    // damit die Anfrage nicht ins Leere läuft.
    if (!hasBackend) {
      window.location.href = buildMailto(form);
      toast.success(
        "Ihr E-Mail-Programm öffnet sich mit der vorbereiteten Anfrage."
      );
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API}/contact`, form);
      toast.success("Vielen Dank! Wir melden uns innerhalb von 24 Stunden.");
      resetForm();
    } catch (err) {
      console.error(err);
      toast.error("Das Formular konnte nicht gesendet werden.", {
        description: `Schreiben Sie uns direkt an ${CONTACT_EMAIL} oder rufen Sie an: ${CONTACT_PHONE}.`,
        action: {
          label: "Per E-Mail senden",
          onClick: () => {
            window.location.href = buildMailto(form);
          },
        },
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-sm text-[#EAB308] font-semibold uppercase tracking-widest">
              Kontakt
            </div>
            <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1A1D20]">
              Bereit für Ihre eigene Solaranlage?
            </h2>
            <p className="mt-5 text-[#4B5563] leading-relaxed">
              Fordern Sie eine kostenlose, unverbindliche Beratung an. Unser
              Team meldet sich innerhalb eines Werktags – per Telefon oder
              direkt mit einer ersten Skizze für Ihr Dach.
            </p>

            <figure className="mt-8 relative rounded-2xl overflow-hidden img-placeholder">
              <img
                src="/images/kontakt-beratung.webp"
                alt="Solarberater von Sonnenwerk zeigt einem Paar am Küchentisch die Dachbelegung auf einem Tablet"
                width="1200"
                height="896"
                loading="lazy"
                decoding="async"
                className="w-full h-[220px] md:h-[260px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1D20]/75 to-transparent" />
              <figcaption className="absolute inset-x-0 bottom-0 p-5 text-white text-sm">
                Erstberatung bei Ihnen zuhause oder digital – kostenlos und
                unverbindlich.
              </figcaption>
            </figure>

            <div className="mt-8 space-y-5">
              <ContactItem
                icon={Phone}
                label="Telefon"
                value={CONTACT_PHONE}
                href={CONTACT_PHONE_HREF}
                testid="contact-phone"
              />
              <ContactItem
                icon={Mail}
                label="E-Mail"
                value={CONTACT_EMAIL}
                href={`mailto:${CONTACT_EMAIL}`}
                testid="contact-email"
              />
              <ContactItem
                icon={MapPin}
                label="Hauptsitz"
                value="Industriestrasse 24, 8005 Zürich"
                testid="contact-address"
              />
            </div>

            <div className="mt-10 p-5 rounded-2xl bg-[#1A1D20] text-white">
              <div className="text-xs uppercase tracking-widest text-[#EAB308] font-semibold">
                Öffnungszeiten
              </div>
              <div className="mt-2 text-sm text-white/80 leading-relaxed">
                Mo–Fr 08:00 – 18:00 Uhr<br />
                Sa nach Vereinbarung
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-white rounded-2xl p-6 md:p-10 border border-black/5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Name *">
                <Input
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Max Muster"
                  className="h-12 rounded-xl"
                  required
                />
              </Field>
              <Field label="E-Mail *">
                <Input
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="max@beispiel.ch"
                  className="h-12 rounded-xl"
                  required
                />
              </Field>
              <Field label="Telefon">
                <Input
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+41 ..."
                  className="h-12 rounded-xl"
                />
              </Field>
              <Field label="Thema">
                <Select
                  value={form.topic}
                  onValueChange={(v) => setForm((f) => ({ ...f, topic: v }))}
                >
                  <SelectTrigger
                    data-testid="contact-topic-trigger"
                    className="h-12 rounded-xl"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PV-Anlage Privat">PV-Anlage Privat</SelectItem>
                    <SelectItem value="PV-Anlage Gewerbe">PV-Anlage Gewerbe</SelectItem>
                    <SelectItem value="Stromspeicher">Stromspeicher</SelectItem>
                    <SelectItem value="Wallbox / E-Mobilität">Wallbox / E-Mobilität</SelectItem>
                    <SelectItem value="Allgemeine Anfrage">Allgemeine Anfrage</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <div className="mt-5">
              <Field label="Nachricht *">
                <Textarea
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Bitte beschreiben Sie kurz Ihr Projekt (Dachgrösse, Standort, ungefährer Stromverbrauch)…"
                  rows={5}
                  className="rounded-xl resize-none"
                  required
                />
              </Field>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="text-xs text-[#4B5563]">
                Mit dem Absenden stimmen Sie unserer Datenschutzerklärung zu.
              </div>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="bg-[#EAB308] hover:bg-[#CA9A06] text-[#1A1D20] rounded-full h-12 px-7 font-semibold"
              >
                {submitting ? "Wird gesendet…" : (
                  <>Anfrage senden <Send size={16} /></>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm text-[#1A1D20]/80 mb-2 block">{label}</Label>
      {children}
    </div>
  );
}

function ContactItem({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-xl bg-[#EAB308]/15 text-[#1A1D20] flex items-center justify-center">
        <Icon size={20} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-[#4B5563]">
          {label}
        </div>
        <div className="font-display font-semibold text-[#1A1D20]">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} data-testid={testid} className="block hover:opacity-80 transition">
      {inner}
    </a>
  ) : (
    <div data-testid={testid}>{inner}</div>
  );
}
